import { useCallback, useEffect, useRef, useState } from 'react';
import { WEB_TO_APP, APP_TO_WEB, sendApplicationEventByBridge } from '@constant/common/bridge-event';
import { IS_REACT_NATIVE_APP } from '@config/environment';
import {
  useCreateSpeakingTurnMutation,
  useSpeakingTurnUploadMutation,
  useCheckSpeakingTurnUploadMutation,
} from '@hook/iyagigo/useApi';
import { ISpeakingTurnData } from '@datatype/iyagigo/api-type';
import { TAudioFormat } from '@datatype/common/api-type';
import { getIsBrowserRecordingSupported, startBrowserRecording, IBrowserRecordingResult } from '@util/audio-recorder';

// ############### type ###############
// RN이 주기적으로 보내는 녹음 상태
// - denied: 이번에 거부(재요청 가능) / blocked: 설정에서 꺼둠(설정 앱으로 보내야 함)
export type TRecordingState = 'recording' | 'finished' | 'granted' | 'denied' | 'blocked' | 'error';

interface IAudioRecordingStatus {
  state: TRecordingState;
  durationMs?: number;
  metering?: number;
  error?: string;
}

interface IFinishAudioRecordingResult {
  status: 'success' | 'error';
  durationMs?: number;
  error?: string;
}

interface IUploadAudioRecordingResult {
  status: 'success' | 'error';
  error?: string;
}

// 화면이 쓰는 마이크 상태. 'idle'은 아직 아무것도 하지 않은 상태.
export type TMicrophoneState = 'idle' | 'recording' | 'denied' | 'blocked' | 'error';

// RN이 결과를 돌려주지 않으면 화면이 영원히 '인식 중'에 갇히므로 상한을 둔다.
const BRIDGE_RESULT_TIMEOUT_MS = 30000;
// 기존 앱은 녹음 중이 아니면 CANCEL에 응답하지 않는다. 정리 대기는 짧게 제한한다.
const RECORDING_CLEANUP_TIMEOUT_MS = 1000;

// 이보다 짧은 녹음은 말이 담겼을 리 없다. 버튼을 눌렀다 바로 뗀 경우를 업로드 전에 거른다.
// 'はい'처럼 짧은 답은 살려야 해서 넉넉히 잡는다.
const MIN_RECORDING_MS = 500;

/**
 * 이보다 길어지면 녹음을 자동으로 끝낸다.
 *
 * 과제형 대화라 한 턴은 길어야 10초 안팎이다. 넘어가면 말하는 중이 아니라 버튼을 못 뗀 상황일 가능성이
 * 높고, 긴 녹음은 전사비가 늘 뿐 아니라 한 턴에 여러 말이 섞여 교정과 맥락이 흐려진다.
 */
export const MAX_RECORDING_MS = 30000;

/** 녹음이 너무 짧아 보내지 않았다는 표시. null(실패)과 구분해 안내 문구를 가른다. */
export const TOO_SHORT_RECORDING = 'TOO_SHORT' as const;

// 브라우저에서 presigned URL로 직접 PUT한다. 서명에 쓰인 contentType을 그대로 헤더에 넣어야 S3가 받아준다.
const uploadBlob = async (
  uploadUrl: string,
  contentType: string,
  blob: Blob,
): Promise<IUploadAudioRecordingResult> => {
  try {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: blob,
    });
    if (!response.ok) return { status: 'error', error: `${response.status}` };
    return { status: 'success' };
  } catch (error) {
    console.error('=== audio upload failed ===', error);
    return { status: 'error', error: 'network' };
  }
};

/**
 * AI 말하기 녹음·업로드 훅
 *
 * 녹음 방식은 실행 환경으로 갈린다. 폰이든 데스크톱이든 브라우저면 브라우저 녹음이다.
 * - 앱(RN WebView): RN이 녹음한다. 웹은 브리지로 START/FINISH/UPLOAD만 요청한다.
 * - 브라우저: MediaRecorder로 직접 녹음하고 presigned URL에 직접 PUT한다.
 *
 * presigned 발급은 어느 쪽이든 웹이 한다. RN에는 토큰이 없기 때문.
 * 턴 생성(=발급)을 녹음 종료 후로 미뤄 탭 즉시 녹음이 시작되게 한다. 이 시점은 어차피 전사를 기다리는
 * 구간이라 왕복 지연이 드러나지 않고, 취소된 녹음이 빈 턴으로 남지도 않는다.
 */
interface IAudioRecordingOptions<T> {
  createUpload: (format: TAudioFormat) => Promise<{ uploadUrl: string; contentType: string; context: T }>;
  checkUpload: (context: T) => Promise<void>;
}

/** 앱·브라우저 녹음은 공유하고 업로드 대상 발급·확인은 각 서비스가 제공한다. */
export const useAudioRecording = <T>(options: IAudioRecordingOptions<T>) => {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const [microphoneState, setMicrophoneState] = useState<TMicrophoneState>('idle');
  const [durationMs, setDurationMs] = useState(0);
  // 녹음 중 음량(dB). 파형 표시에 사용한다.
  const [metering, setMetering] = useState(0);
  // API뿐 아니라 브리지 응답·파일 업로드까지 포함한 녹음 종료 작업의 진행 여부.
  const [isFinishingRecording, setIsFinishingRecording] = useState(false);
  // STOP/UPLOAD 요청과 RN 응답을 잇는 resolver
  const recordingResolverRef = useRef<((result: IFinishAudioRecordingResult) => void) | null>(null);
  const uploadResolverRef = useRef<((result: IUploadAudioRecordingResult) => void) | null>(null);
  // 브라우저 녹음 핸들. 앱에서는 사용하지 않는다.
  const browserRecordingRef = useRef<Awaited<ReturnType<typeof startBrowserRecording>> | null>(null);
  const isStartingRecordingRef = useRef(false);
  // 화면 이탈 중 시작 대기가 끝나더라도 새 녹음을 시작하지 않는다.
  const recordingRequestRef = useRef(0);
  const cancelStartWaitRef = useRef<(() => void) | null>(null);

  // RN이 흘려보내는 녹음 상태 수신 (주기 전송이라 Promise가 아닌 리스너로 받는다)
  useEffect(() => {
    if (!IS_REACT_NATIVE_APP) return;

    const handleStatus = (event: CustomEvent<IAudioRecordingStatus>) => {
      const { state, durationMs: duration, metering: level } = event.detail;

      if (state === 'granted') {
        // 권한 허용 알림은 녹음 상태를 바꾸지 않고, 권한 거부 상태만 해제한다.
        setMicrophoneState((previous) => (previous === 'blocked' || previous === 'denied' ? 'idle' : previous));
        return;
      }
      if (state === 'recording') {
        setMicrophoneState('recording');
        setDurationMs(duration ?? 0);
        setMetering(level ?? 0);
        return;
      }
      if (state === 'denied' || state === 'blocked' || state === 'error') {
        setMicrophoneState(state);
        return;
      }
      // finished
      setMicrophoneState('idle');
      setMetering(0);
    };

    const handleRecordingResult = (event: CustomEvent<IFinishAudioRecordingResult>) => {
      recordingResolverRef.current?.(event.detail);
      recordingResolverRef.current = null;
    };

    const handleUploadResult = (event: CustomEvent<IUploadAudioRecordingResult>) => {
      uploadResolverRef.current?.(event.detail);
      uploadResolverRef.current = null;
    };

    window.addEventListener(APP_TO_WEB.AUDIO_RECORDING_STATUS, handleStatus as EventListener);
    window.addEventListener(APP_TO_WEB.FINISH_AUDIO_RECORDING_RESULT, handleRecordingResult as EventListener);
    window.addEventListener(APP_TO_WEB.UPLOAD_AUDIO_RECORDING_RESULT, handleUploadResult as EventListener);

    return () => {
      window.removeEventListener(APP_TO_WEB.AUDIO_RECORDING_STATUS, handleStatus as EventListener);
      window.removeEventListener(APP_TO_WEB.FINISH_AUDIO_RECORDING_RESULT, handleRecordingResult as EventListener);
      window.removeEventListener(APP_TO_WEB.UPLOAD_AUDIO_RECORDING_RESULT, handleUploadResult as EventListener);
    };
  }, []);

  // 브라우저 녹음 중 경과시간. 앱은 RN이 status로 durationMs를 흘려보내지만 브라우저에는 그 경로가 없다.
  useEffect(() => {
    if (IS_REACT_NATIVE_APP || microphoneState !== 'recording') return;

    const startedAt = Date.now();
    const timer = window.setInterval(() => setDurationMs(Date.now() - startedAt), 100);
    return () => window.clearInterval(timer);
  }, [microphoneState]);

  // 학습 화면 진입 시 권한을 미리 확보한다.
  // 탭하는 순간 권한 팝업이 끼어들면 말할 준비를 한 흐름이 끊기기 때문.
  const checkMicrophonePermission = useCallback(() => {
    // 브라우저는 getUserMedia를 부르는 순간 팝업이 뜬다. 미리 띄우면 말할 준비도 전에 권한을 묻게 되므로 하지 않는다.
    if (!IS_REACT_NATIVE_APP) return;
    sendApplicationEventByBridge(WEB_TO_APP.CHECK_MICROPHONE_PERMISSION);
  }, []);

  // blocked 상태에서 OS 설정 앱으로 보낸다.
  const openSystemSetting = useCallback(() => {
    // 브라우저에는 설정 앱으로 보낼 방법이 없다. 화면이 주소창 권한 안내를 대신 띄운다.
    if (!IS_REACT_NATIVE_APP) return;
    sendApplicationEventByBridge(WEB_TO_APP.OPEN_SYSTEM_SETTING);
  }, []);

  const startRecording = useCallback(async () => {
    if (isStartingRecordingRef.current) return;
    isStartingRecordingRef.current = true;
    const request = ++recordingRequestRef.current;
    try {
      if (IS_REACT_NATIVE_APP) {
        // 웹은 대기 상태여도 앱에 이전 녹음이 남을 수 있다. 정리 후 새 녹음을 요청한다.
        await new Promise<void>((resolve) => {
          const complete = () => {
            clearTimeout(timer);
            window.removeEventListener(APP_TO_WEB.AUDIO_RECORDING_STATUS, handleStatus as EventListener);
            cancelStartWaitRef.current = null;
            resolve();
          };
          const handleStatus = (event: CustomEvent<IAudioRecordingStatus>) => {
            if (event.detail.state === 'finished') complete();
          };
          const timer = setTimeout(complete, RECORDING_CLEANUP_TIMEOUT_MS);
          cancelStartWaitRef.current = complete;
          window.addEventListener(APP_TO_WEB.AUDIO_RECORDING_STATUS, handleStatus as EventListener);
          sendApplicationEventByBridge(WEB_TO_APP.CANCEL_AUDIO_RECORDING);
        });
        if (recordingRequestRef.current !== request) return;
        setDurationMs(0);
        setMetering(0);
        sendApplicationEventByBridge(WEB_TO_APP.START_AUDIO_RECORDING);
        return;
      }
      if (!getIsBrowserRecordingSupported()) {
        console.error('=== browser recording unsupported ===', {
          hasMediaDevices: !!navigator.mediaDevices,
          hasGetUserMedia: !!navigator.mediaDevices?.getUserMedia,
          hasMediaRecorder: typeof MediaRecorder !== 'undefined',
          isSecureContext: window.isSecureContext,
          origin: window.location.origin,
        });
        setMicrophoneState('error');
        return;
      }

      browserRecordingRef.current?.cancel();
      browserRecordingRef.current = null;
      const recording = await startBrowserRecording(setMetering);
      if (recordingRequestRef.current !== request) {
        recording.cancel();
        return;
      }
      browserRecordingRef.current = recording;
      setDurationMs(0);
      setMicrophoneState('recording');
    } catch (error) {
      if (recordingRequestRef.current !== request) return;
      console.error('=== browser recording start failed ===', error);
      // 권한 거부는 NotAllowedError. 브라우저는 한 번 거부하면 주소창에서 풀어야 하므로 blocked로 둔다.
      const isDenied = error instanceof DOMException && error.name === 'NotAllowedError';
      setMicrophoneState(isDenied ? 'blocked' : 'error');
    } finally {
      isStartingRecordingRef.current = false;
    }
  }, []);

  /**
   * 녹음을 끝내고 업로드 대상 context와 브라우저 Blob을 돌려준다.
   *
   * 녹음을 멈추는 방식만 앱(브리지)과 브라우저(MediaRecorder)로 갈리고,
   * presigned 발급·업로드 확인 콜백은 양쪽이 같은 경로를 쓴다.
   *
   * 전사·분석·응답은 화면에서 useApi.ts의 mutation으로 이어서 호출한다.
   */
  const finishRecording = useCallback(async (): Promise<
    { context: T; blob: Blob | null } | typeof TOO_SHORT_RECORDING | null
  > => {
    const request = recordingRequestRef.current;
    setIsFinishingRecording(true);
    try {
      // 앱은 브리지로 녹음을 멈추고 파일을 RN이 들고 있는다. 브라우저는 여기서 blob을 직접 받는다.
      let browserRecording: IBrowserRecordingResult | null = null;
      let recordedMs = 0;

      if (IS_REACT_NATIVE_APP) {
        const recording = await new Promise<IFinishAudioRecordingResult>((resolve) => {
          const timer = setTimeout(() => {
            recordingResolverRef.current = null;
            resolve({ status: 'error', error: 'timeout' });
          }, BRIDGE_RESULT_TIMEOUT_MS);

          recordingResolverRef.current = (received) => {
            clearTimeout(timer);
            resolve(received);
          };
          sendApplicationEventByBridge(WEB_TO_APP.FINISH_AUDIO_RECORDING);
        });

        setMicrophoneState('idle');
        if (recording.status !== 'success') return null;
        recordedMs = recording.durationMs ?? 0;
      } else {
        const recorder = browserRecordingRef.current;
        browserRecordingRef.current = null;
        setMicrophoneState('idle');
        setMetering(0);
        if (!recorder) return null;

        try {
          browserRecording = await recorder.finish();
          recordedMs = browserRecording.durationMs;
        } catch (error) {
          console.error('=== browser recording failed ===', error);
          return null;
        }
      }

      // 눌렀다 바로 뗀 녹음은 업로드도 하지 않는다. 무음을 보내면 전사가 빈 문자열이나 환각을 돌려주는데,
      // 그 쓰레기 턴이 대화에 남고 context까지 오염시켜 이후 모든 턴에 번진다.
      // 실패가 아니라 "말이 안 담겼다"이므로 호출부가 안내를 가를 수 있게 사유를 구분해 돌려준다.
      if (recordedMs < MIN_RECORDING_MS) return TOO_SHORT_RECORDING;

      if (request !== recordingRequestRef.current) return null;
      const format: TAudioFormat = browserRecording?.format ?? 'MP4';
      const target = await optionsRef.current.createUpload(format);
      if (request !== recordingRequestRef.current) return null;

      const upload = browserRecording
        ? await uploadBlob(target.uploadUrl, target.contentType, browserRecording.blob)
        : await new Promise<IUploadAudioRecordingResult>((resolve) => {
            const timer = setTimeout(() => {
              uploadResolverRef.current = null;
              resolve({ status: 'error', error: 'timeout' });
            }, BRIDGE_RESULT_TIMEOUT_MS);

            uploadResolverRef.current = (received) => {
              clearTimeout(timer);
              resolve(received);
            };
            sendApplicationEventByBridge(WEB_TO_APP.UPLOAD_AUDIO_RECORDING, {
              uploadUrl: target.uploadUrl,
              contentType: target.contentType,
            });
          });
      if (request !== recordingRequestRef.current || upload.status !== 'success') return null;

      await optionsRef.current.checkUpload(target.context);
      if (request !== recordingRequestRef.current) return null;
      return { context: target.context, blob: browserRecording?.blob ?? null };
    } catch (error) {
      console.error('=== audio recording upload failed ===', error);
      return null;
    } finally {
      setIsFinishingRecording(false);
    }
  }, []);

  // 녹음을 버린다(업로드하지 않음). 화면 이탈 시에도 호출해야 마이크가 풀린다.
  const cancelRecording = useCallback(() => {
    recordingRequestRef.current += 1;
    cancelStartWaitRef.current?.();
    // 브라우저는 스트림을 직접 끊어야 탭의 마이크 표시가 사라진다.
    if (browserRecordingRef.current) {
      browserRecordingRef.current.cancel();
      browserRecordingRef.current = null;
    }
    if (IS_REACT_NATIVE_APP) sendApplicationEventByBridge(WEB_TO_APP.CANCEL_AUDIO_RECORDING);
    recordingResolverRef.current?.({ status: 'error', error: 'cancelled' });
    uploadResolverRef.current?.({ status: 'error', error: 'cancelled' });
    recordingResolverRef.current = null;
    uploadResolverRef.current = null;
    setMicrophoneState('idle');
    setMetering(0);
  }, []);

  return {
    microphoneState,
    durationMs,
    metering,
    isFinishingRecording,
    checkMicrophonePermission,
    openSystemSetting,
    startRecording,
    finishRecording,
    cancelRecording,
  };
};

/** 기존 AI 말하기 계약: 녹음 종료 후 학습자 턴을 생성하고 그 턴으로 업로드한다. */
export const useAudioTranscription = (sessionKey: string) => {
  const createTurnMutation = useCreateSpeakingTurnMutation(sessionKey);
  const uploadMutation = useSpeakingTurnUploadMutation(sessionKey);
  const checkUploadMutation = useCheckSpeakingTurnUploadMutation(sessionKey);
  const recording = useAudioRecording<ISpeakingTurnData>({
    createUpload: async (format) => {
      const { data: turn } = await createTurnMutation.mutateAsync(undefined);
      const { data: target } = await uploadMutation.mutateAsync({ turnKey: turn.key, format });
      return { ...target, context: turn };
    },
    checkUpload: async (turn) => {
      await checkUploadMutation.mutateAsync(turn.key);
    },
  });
  const { finishRecording } = recording;
  return {
    ...recording,
    finishRecording: useCallback(async (): Promise<ISpeakingTurnData | typeof TOO_SHORT_RECORDING | null> => {
      const result = await finishRecording();
      if (result === TOO_SHORT_RECORDING) return TOO_SHORT_RECORDING;
      if (!result) return null;
      return result.context;
    }, [finishRecording]),
  };
};
